import type { ZudokuConfig, ZudokuPlugin } from 'zudoku';
import Home from './src/home/Home';
import { DOCS_BRAND } from './src/brand';
import { generateCodeSnippet } from './src/codeSnippets';
import { lightTheme, darkTheme, customCss } from './src/theme';

// docs.swaps.app — Swaps developer documentation, generated from the same OpenAPI contract
// that gates production (docs/api/openapi/swaps-v1.yaml, copied in at prebuild by
// scripts/prebuild.mjs — see README.md "How content flows"). Design ported from the approved
// mock at swaps-design-archive/dashboard-redesign/work/docs-design (A4 §1b, G 04.09).

// One permanent left-sidebar tree, no top tab row (architect ruling, 04.09 — the approved
// design has a single always-visible sidebar, not Zudoku's default top-tab-per-root-item
// switcher, and the mobile drawer must list every section, not just the active one).
//
// Two Zudoku constraints collide here and this is the resolution:
//  1. "Items placed at the root of [the navigation] array are rendered as tabs, while nested
//     items … construct the sidebar tree" (Zudoku docs, configuration/navigation.mdx) — the
//     LEFT SIDEBAR only ever shows the children of whichever root item is "active" for the
//     current route. A plain root doc/link (no children) therefore shows an EMPTY sidebar.
//  2. The mobile nav drawer renders the navigation array's ROOT items as its own flat list —
//     confirmed empirically: wrapping the whole tree in one root category to suppress the
//     desktop tab row collapses the mobile drawer to a single "Docs" link, because a lone root
//     category with no explicit `link` is treated as a redirect to its first child rather than
//     an expandable section (there is no config flag to make it expand instead).
//
// The fix: keep every real section as its OWN root item (so the mobile drawer lists all of
// them, exactly the working shape verified in qa/), but give every root item that owns
// in-app pages an IDENTICAL `items: sidebarTree` — the same six-entry list, reused by
// reference — plus an explicit `link` to its own landing page. Whichever tab ends up "active"
// therefore renders the same sidebar tree, so desktop reads as one permanent sidebar even
// though Zudoku is still, mechanically, switching between per-tab sidebars. The (now several)
// root tabs are hidden with a scoped rule in theme.ts's customCss — the header's own
// brand/search/theme-toggle bar is a separate, earlier element and is unaffected.
const sidebarTree = [
  {
    type: 'custom-page' as const,
    path: '/',
    element: <Home />,
    label: 'Home',
    icon: 'house' as const,
  },
  { type: 'doc' as const, file: 'get-started', icon: 'terminal' as const },
  {
    type: 'category' as const,
    label: 'Concepts',
    icon: 'book-open' as const,
    items: [
      { type: 'doc' as const, file: 'authentication', icon: 'key' as const },
      { type: 'doc' as const, file: 'environments', icon: 'server' as const },
      { type: 'doc' as const, file: 'test-mode', icon: 'flask-conical' as const },
      { type: 'doc' as const, file: 'conventions', icon: 'list-checks' as const },
      { type: 'doc' as const, file: 'errors', icon: 'alert-triangle' as const },
      { type: 'doc' as const, file: 'events-webhooks', icon: 'webhook' as const },
      { type: 'doc' as const, file: 'providers-coverage', icon: 'globe' as const },
    ],
  },
  {
    type: 'category' as const,
    label: 'Products',
    icon: 'layers' as const,
    items: [
      { type: 'doc' as const, file: 'products/customers', icon: 'user-check' as const },
      { type: 'doc' as const, file: 'products/payment-links', icon: 'link' as const },
      { type: 'doc' as const, file: 'products/pay-invoice', icon: 'credit-card' as const },
      { type: 'doc' as const, file: 'products/payroll', icon: 'banknote' as const },
      { type: 'doc' as const, file: 'products/wallet', icon: 'wallet' as const },
      { type: 'doc' as const, file: 'products/buy-sell', icon: 'repeat' as const },
      { type: 'doc' as const, file: 'products/crypto-processing', icon: 'coins' as const },
    ],
  },
  { type: 'doc' as const, file: 'agents-mcp', icon: 'bot' as const },
  { type: 'link' as const, to: '/reference', label: 'API reference', icon: 'code' as const },
  { type: 'doc' as const, file: 'changelog', icon: 'clipboard-list' as const },
  {
    type: 'link' as const,
    to: 'https://swaps.app/legal/api-terms',
    label: 'API terms',
    icon: 'scale' as const,
    target: '_blank' as const,
  },
];

const sidebarNavigation: ZudokuConfig['navigation'] = [
  {
    type: 'category',
    label: 'Home',
    icon: 'house',
    link: { type: 'link', to: '/' },
    items: sidebarTree,
  },
  {
    type: 'category',
    label: 'Get started',
    icon: 'terminal',
    link: { type: 'doc', file: 'get-started' },
    items: sidebarTree,
  },
  {
    type: 'category',
    label: 'Concepts',
    icon: 'book-open',
    link: { type: 'doc', file: 'authentication' },
    items: sidebarTree,
  },
  {
    type: 'category',
    label: 'Products',
    icon: 'layers',
    link: { type: 'doc', file: 'products/payment-links' },
    items: sidebarTree,
  },
  {
    type: 'category',
    label: 'Agents & MCP',
    icon: 'bot',
    link: { type: 'doc', file: 'agents-mcp' },
    items: sidebarTree,
  },
  // A plain `{ type: 'link' }` root entry here (as it briefly was) has no `items`, so Zudoku's
  // `useCurrentNavigation` finds no `category` to source a sidebar from once the OAS plugin's
  // own `getNavigation` takes over `/reference/*` — the plugin's tag-category list REPLACES the
  // sidebar outright, breaking the one-permanent-sidebar ruling (P1-3, design-parity QA 04.09).
  // Zudoku's own merge (`ZudokuContext.ts` `useCurrentNavigation`) is
  // `[...topNavItem.items, ...pluginData]` — it can prepend the active root category's static
  // `items` ahead of the plugin's own navigation, but has no config surface to nest the plugin's
  // tag list one level deeper as children of a single row; this `category` (mirroring every
  // other root entry's `items: sidebarTree`) is what makes it prepend `sidebarTree` rather than
  // nothing. The tag categories still render, appended after `sidebarTree` rather than indented
  // under this row — the closest true nesting Zudoku 0.86 exposes; documented in
  // docs/api/DOCS-PLATFORM.md.
  {
    type: 'category',
    label: 'API reference',
    icon: 'code',
    link: { type: 'link', to: '/reference' },
    items: sidebarTree,
  },
  {
    type: 'category',
    label: 'Changelog',
    icon: 'clipboard-list',
    link: { type: 'doc', file: 'changelog' },
    items: sidebarTree,
  },
  {
    type: 'link',
    to: 'https://swaps.app/legal/api-terms',
    label: 'API terms',
    icon: 'scale',
    target: '_blank',
  },
];

// Two mounts, one switch (DOCS-CUTOVER-1, 05.10; architect ruling 04.09 "one programme cutover").
// Previews, staging.swaps.app and every non-production build ship this site under `/_docs` on the
// same deployment as the marketing site, `noindex`ed by the root vercel.json — that is where G
// reviews it (<preview>.vercel.app/_docs/). A PRODUCTION build mounts it at the root of
// docs.swaps.app instead: scripts/build-docs-into-dist.mjs derives the mount from VERCEL_ENV
// (production -> '', anything else -> '/_docs'; an explicit DOCS_BASE_PATH overrides both) and hands
// it to this file as ZUDOKU_PUBLIC_DOCS_BASE_PATH. The docs therefore go live with the
// dashboard-v2 merge itself (§52.69) — no Vercel env var is set by hand. DOCS-PLATFORM.md §deploy.
//
// Why the ZUDOKU_PUBLIC_ prefix (checked against a real build, 05.10): Zudoku loads this file in
// Node AND bundles it into the client and prerender builds, and only ZUDOKU_PUBLIC_* /
// ZUPLO_PUBLIC_* variables reach the bundled copy (`loadEnv` in node_modules/zudoku/src/config/
// loader.ts, `define`d in src/vite/config.ts). The unprefixed DOCS_BASE_PATH this file used to read
// was undefined inside the bundle, so DOCS_BASE_PATH="" built flat asset URLs (Node side) under a
// router basename, nav links, redirect pages and robots meta still on `/_docs` (bundle side) — a
// page that renders nothing after hydration on a host root. The build script's
// `assertDocsBuildMode` now fails the build on exactly that hybrid.
const DOCS_BASE_PATH = process.env.ZUDOKU_PUBLIC_DOCS_BASE_PATH ?? '/_docs';
const DOCS_ORIGIN = 'https://docs.swaps.app';

// React Router normally removes `basePath` from `location.pathname`, but keep the cleanup here
// as a fail-safe for prerender/build contexts that expose the mounted path. Discovery URLs always
// name the public docs host and never the preview-only `/_docs` mount.
const cleanPublicPath = (pathname: string) => {
  let cleanPath = pathname || '/';
  if (
    DOCS_BASE_PATH &&
    (cleanPath === DOCS_BASE_PATH || cleanPath.startsWith(`${DOCS_BASE_PATH}/`))
  ) {
    cleanPath = cleanPath.slice(DOCS_BASE_PATH.length) || '/';
  }
  if (!cleanPath.startsWith('/')) cleanPath = `/${cleanPath}`;
  return cleanPath === '/' ? cleanPath : cleanPath.replace(/\/+$/, '');
};

// Route discovery metadata plus the v4 head tags Zudoku's `metadata` has no field for. A plugin's
// getHead lands in every prerendered page, including generated OpenAPI routes. Public discovery
// paths stay independent of the preview-only basePath; asset paths keep that prefix.
const discoveryHead: ZudokuPlugin = {
  getHead: ({ location }) => {
    const publicPath = cleanPublicPath(location.pathname);
    const isReferencePage = publicPath === '/reference' || publicPath.startsWith('/reference/');

    return (
      <>
        <link rel="canonical" href={`${DOCS_ORIGIN}${publicPath}`} />
        {isReferencePage && (
          <link rel="alternate" type="text/markdown" href={`${publicPath}.md`} />
        )}
        <meta name="theme-color" content={DOCS_BRAND.themeColor} />
        {DOCS_BRAND.pngIcons.map((icon) => (
          <link
            key={icon.href}
            rel="icon"
            type="image/png"
            sizes={icon.sizes}
            href={`${DOCS_BASE_PATH}${icon.href}`}
          />
        ))}
        <link rel="apple-touch-icon" href={`${DOCS_BASE_PATH}${DOCS_BRAND.appleTouchIcon}`} />
        <meta property="og:image" content={DOCS_BRAND.ogImage.url} />
        <meta property="og:image:type" content={DOCS_BRAND.ogImage.type} />
        <meta property="og:image:width" content={String(DOCS_BRAND.ogImage.width)} />
        <meta property="og:image:height" content={String(DOCS_BRAND.ogImage.height)} />
        <meta property="og:image:alt" content={DOCS_BRAND.ogImage.alt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={DOCS_BRAND.ogImage.url} />
      </>
    );
  },
};

const config: ZudokuConfig = {
  basePath: DOCS_BASE_PATH === '' ? undefined : DOCS_BASE_PATH,

  site: {
    title: 'Swaps Docs',
    // The v4 lockup, Ink on light and Paper on dark (src/brand.ts). No `width`: Zudoku would write it inline, and the
    // size comes from src/theme.ts instead (as tall as the swaps.app header draws it, smaller below 1024px).
    logo: {
      src: DOCS_BRAND.logo,
      alt: 'swaps.app docs home',
    },
    footer: {
      columns: [
        {
          title: 'Docs',
          links: [
            { label: 'Get started', href: '/get-started' },
            { label: 'API reference', href: '/reference' },
            { label: 'Agents & MCP', href: '/agents-mcp' },
            { label: 'Changelog', href: '/changelog' },
          ],
        },
        {
          title: 'Swaps',
          links: [
            { label: 'swaps.app', href: 'https://swaps.app' },
            { label: 'API terms', href: 'https://swaps.app/legal/api-terms' },
            { label: 'Status', href: 'https://swaps.app/status' },
          ],
        },
      ],
    },
  },

  metadata: {
    // Zudoku applies this as a template whenever a page supplies its own title. The default is
    // the custom docs landing page, which otherwise has no route-level <title> at all.
    title: '%s | Swaps API docs',
    defaultTitle: 'Swaps API documentation | Swaps',
    description:
      'Move money over an API your agent can use — payment links, payouts, payroll, wallet, buy & sell and crypto processing.',
    favicon: DOCS_BRAND.favicon,
    applicationName: 'Swaps Docs',
    generator: 'Zudoku',
    // One switch (P2, design-parity QA 04.09): under `/_docs` this site must stay out of search
    // results — the root vercel.json `X-Robots-Tag` header already does that at the HTTP layer,
    // and this `<meta name="robots">` must not contradict it. Tied to the same constant as
    // `basePath`, so the production (docs.swaps.app root) build is the only indexable one.
    robots: DOCS_BASE_PATH === '' ? 'index, follow' : 'noindex, nofollow',
  },

  theme: {
    light: lightTheme,
    dark: darkTheme,
    fonts: {
      sans: 'Inter',
      mono: 'JetBrains Mono',
    },
    customCss,
  },

  syntaxHighlighting: {
    themes: {
      light: 'github-light',
      dark: 'github-dark',
    },
  },

  search: {
    type: 'pagefind',
    maxSubResults: 3,
  },

  sitemap: {
    siteUrl: 'https://docs.swaps.app',
    changefreq: 'daily',
    priority: 0.7,
  },

  docs: {
    files: 'pages/**/*.mdx',
    publishMarkdown: true,
    defaultOptions: {
      toc: true,
      copyPage: true,
      showLastModified: false,
    },
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
      title: 'Swaps API docs',
      description: 'Move money over an API your agent can use.',
    },
  },

  apis: {
    type: 'file',
    input: './openapi/swaps-v1.yaml',
    path: '/reference',
    options: {
      examplesLanguage: 'shell',
      supportedLanguages: [
        { value: 'shell', label: 'cURL' },
        { value: 'typescript', label: 'TypeScript' },
        { value: 'python', label: 'Python' },
      ],
      disableSecurity: false,
      disableSidecar: false,
      // Off until api-v1's CORS admits https://docs.swaps.app (DOCS-CORS-1-T, supabase/functions/
      // _shared/cors.ts): a "Try it" call from docs.swaps.app is refused by the browser before it
      // reaches the API. Re-enable in the same change that ships that origin to production.
      disablePlayground: true,
      expandAllTags: false,
      showInfoPage: true,
      schemaDownload: { enabled: true },
      generateCodeSnippet,
    },
  },

  redirects: [
    { from: '/docs', to: '/get-started' },
    { from: '/api', to: '/reference' },
  ],

  // One permanent left-sidebar tree, no top tab row (architect ruling, 04.09 — the approved
  // design has a single always-visible sidebar, not Zudoku's default top-tab-per-root-item
  // switcher). Zudoku always renders one tab per *root* navigation item ("Items placed at the
  // root of this array are rendered as tabs, while nested items … construct the sidebar tree" —
  // Zudoku docs, configuration/navigation.mdx) — there is no config flag to suppress that for
  // more than one root item. So every real entry is nested one level inside a single wrapping
  // root category, producing exactly one (lone) tab, which is then hidden with a scoped rule in
  // theme.ts's customCss (`[data-testid="top-navigation"]` / the tablist role) — the header's
  // brand, search and theme toggle live in a separate slot and are unaffected.
  navigation: sidebarNavigation,

  plugins: [discoveryHead],
};

export default config;
